import React, { useState } from 'react'
import services from '../data/services.json'
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"
import { Icon } from '@iconify/react';
import { useItemsContext } from '../hooks/useItemsContext'
import "react-phone-input-2/lib/style.css";
import { SHOW_PRICES } from '../config/priceConfig';

const ACCENT = '#525ceb';
const ACCENT_PURPLE = '#a855f7';

/* ─── Field wrapper ─────────────────────────────────────────────────── */
const Field = ({ id, label, icon, children, required }) => (
    <div className="relative">
        <label
            htmlFor={id}
            className="flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest mb-2"
            style={{ color: ACCENT }}
        >
            <Icon icon={icon} className="text-sm" />
            {label}
            {required && <span style={{ color: ACCENT_PURPLE }}>*</span>}
        </label>
        {children}
    </div>
);

/* ─── Styled input ───────────────────────────────────────────────────── */
const InputField = ({ id, type = 'text', placeholder, onChange, required, min, max, value, dir }) => {
    const [focused, setFocused] = useState(false);
    return (
        <input
            id={id}
            type={type}
            placeholder={placeholder}
            name={id}
            onChange={onChange}
            required={required}
            min={min}
            max={max}
            value={value}
            dir={dir}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className="w-full px-4 py-3 rounded-2xl text-sm font-medium placeholder-gray-500 dark:placeholder-gray-600 text-darktheme dark:text-white"
            style={{
                background: 'rgba(82,92,235,0.04)',
                border: focused ? `1.5px solid ${ACCENT}` : '1.5px solid rgba(82,92,235,0.15)',
                outline: 'none',
                boxShadow: focused ? `0 0 0 3px rgba(82,92,235,0.1)` : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
        />
    );
};

/* ─── Styled textarea ────────────────────────────────────────────────── */
const TextareaField = ({ id, placeholder, onChange, dir }) => {
    const [focused, setFocused] = useState(false);
    return (
        <textarea
            id={id}
            placeholder={placeholder}
            name={id}
            onChange={onChange}
            rows={4}
            dir={dir}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            className="w-full px-4 py-3 rounded-2xl text-sm font-medium placeholder-gray-500 dark:placeholder-gray-600 text-darktheme dark:text-white"
            style={{
                background: 'rgba(82,92,235,0.04)',
                border: focused ? `1.5px solid ${ACCENT}` : '1.5px solid rgba(82,92,235,0.15)',
                outline: 'none',
                boxShadow: focused ? `0 0 0 3px rgba(82,92,235,0.1)` : 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
        />
    );
};

/* ─── Main Component ────────────────────────────────────────────────── */
const RequestForm = ({ languageText, language, api }) => {
    const [quantity, setQuantity] = useState(1);
    const { dispatch } = useItemsContext()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false)
    const [submission, setSubmission] = useState(false)
    const [error, setError] = useState(null)
    const navigate = useNavigate();

    const { id } = useParams();
    const service = Object.values(services)
        .flat()
        .find((item) => item.id.toString() === id.toString());

    if (!service) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-gray-400">{language === 'ar' ? 'الخدمة غير موجودة!' : 'Service not found!'}</p>
            </div>
        );
    }

    const handleQuantityChange = (e) => {
        let value = parseInt(e.target.value, 10);
        if (value < 1) value = 1;
        if (value > 5) value = 5;
        setQuantity(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const numericPrice = parseFloat(service.price.replace(/[^0-9.]/g, ''));
        const item = { name, email, phone, quantity, notes, service: service.name, serviceId: id, price: numericPrice };

        const response = await fetch(`${api}/api/request`, {
            method: "POST",
            body: JSON.stringify(item),
            headers: { 'Content-Type': 'application/json' }
        });
        const json = await response.json();

        if (!response.ok) {
            setError(json.error);
            setSubmitting(false);
        } else {
            setError(null);
            dispatch({ type: 'CREATE_FORM', collection: "requests", payload: json });
            setSubmitting(false);
            setSubmission(true);
            setTimeout(() => {
                setSubmission(false);
                navigate('/services');
            }, 3000);
        }
    };

    const isAr = language === 'ar';
    const dir = isAr ? 'rtl' : 'ltr';

    const pageVariant = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.07 } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.25 } }
    };
    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
    };

    return (
        <motion.div
            variants={pageVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            dir={dir}
            className="min-h-screen relative flex items-start justify-center pt-12 pb-24 px-4"
        >
            {/* Ambient orbs */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.06] blur-[120px]"
                    style={{ background: `radial-gradient(circle, ${ACCENT}, transparent)` }} />
                <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full opacity-[0.06] blur-[120px]"
                    style={{ background: `radial-gradient(circle, ${ACCENT_PURPLE}, transparent)` }} />
            </div>

            <div className="w-full max-w-xl relative z-10">

                {/* ── PAGE HEADER ─────────────────────────── */}
                <motion.div variants={fadeUp} className={isAr ? 'text-right mb-8' : 'text-left mb-8'}>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest mb-6 transition-colors duration-200 hover:opacity-70"
                        style={{ color: ACCENT }}
                    >
                        <Icon icon={isAr ? 'lucide:arrow-right' : 'lucide:arrow-left'} />
                        {isAr ? 'رجوع' : 'Back'}
                    </button>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] mb-2" style={{ color: ACCENT }}>
                        {isAr ? '— طلب خدمة —' : '— Service Request —'}
                    </p>
                    <h1 className="text-3xl md:text-4xl font-black text-darktheme dark:text-white leading-tight">
                        {isAr ? 'أرسل طلبك' : 'Submit Your Request'}
                    </h1>
                    <p className="text-sm text-gray-500 mt-2 font-medium">
                        {isAr ? 'سنتواصل معك في أقرب وقت ممكن.' : "We'll get back to you as soon as possible."}
                    </p>
                </motion.div>

                {/* ── SERVICE BANNER ──────────────────────── */}
                <motion.div variants={fadeUp}
                    className="rounded-2xl p-4 mb-6 flex items-center gap-4"
                    style={{
                        background: 'linear-gradient(135deg, rgba(82,92,235,0.08) 0%, rgba(168,85,247,0.05) 100%)',
                        border: '1px solid rgba(82,92,235,0.2)',
                    }}
                >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-2xl"
                        style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_PURPLE})`, boxShadow: '0 8px 24px rgba(82,92,235,0.35)' }}>
                        <Icon icon={service.icon} />
                    </div>
                    <div className={`flex-1 min-w-0 ${isAr ? 'text-right' : 'text-left'}`}>
                        <p className="text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: ACCENT }}>
                            {isAr ? 'الخدمة المطلوبة' : 'Selected Service'}
                        </p>
                        <h3 className="font-black text-darktheme dark:text-white text-base leading-tight truncate">
                            {isAr ? service.arabicName : service.name}
                        </h3>
                        {SHOW_PRICES && (
                            <p className="text-xs font-semibold mt-0.5" style={{ color: ACCENT_PURPLE }}>{service.price}</p>
                        )}
                    </div>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>
                        <Icon icon="lucide:check" className="text-emerald-400 text-xs" />
                    </div>
                </motion.div>

                {/* ── FORM CARD ───────────────────────────── */}
                <motion.div variants={fadeUp}
                    className="rounded-3xl p-6 md:p-8"
                    style={{
                        background: 'rgba(255,255,255,0.025)',
                        border: '1px solid rgba(82,92,235,0.1)',
                        boxShadow: '0 40px 80px rgba(0,0,0,0.1)',
                        backdropFilter: 'blur(20px)',
                    }}
                >
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">

                        <Field id="name" label={isAr ? 'الاسم الكامل' : 'Full Name'} icon="lucide:user" required>
                            <InputField
                                id="name"
                                placeholder={isAr ? 'أدخل اسمك الكامل...' : 'Enter your full name...'}
                                onChange={(e) => setName(e.target.value)}
                                required
                                dir={dir}
                            />
                        </Field>

                        <Field id="email" label={isAr ? 'البريد الإلكتروني' : 'Email Address'} icon="lucide:mail" required>
                            <InputField
                                id="email"
                                type="email"
                                placeholder={isAr ? 'أدخل بريدك الإلكتروني...' : 'Enter your email address...'}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                dir={dir}
                            />
                        </Field>

                        <Field id="phone" label={isAr ? 'رقم واتساب' : 'WhatsApp Number'} icon="ic:baseline-whatsapp" required>
                            <InputField
                                id="phone"
                                type="number"
                                placeholder={isAr ? 'أدخل رقم واتساب...' : 'Enter your WhatsApp number...'}
                                onChange={(e) => setPhone(e.target.value)}
                                required
                                dir={dir}
                            />
                        </Field>

                        <Field id="quantity" label={isAr ? 'الكمية' : 'Quantity'} icon="lucide:layers" required>
                            <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                                <button
                                    type="button"
                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 transition-transform duration-150 hover:scale-110 active:scale-95"
                                    style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_PURPLE})`, boxShadow: '0 4px 16px rgba(82,92,235,0.35)' }}
                                >
                                    <Icon icon="lucide:minus" className="text-base" />
                                </button>
                                <div className="flex-1">
                                    <InputField
                                        id="quantity"
                                        type="number"
                                        placeholder="1"
                                        onChange={handleQuantityChange}
                                        required
                                        min={1}
                                        max={5}
                                        value={quantity}
                                        dir="ltr"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setQuantity(q => Math.min(5, q + 1))}
                                    className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 transition-transform duration-150 hover:scale-110 active:scale-95"
                                    style={{ background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_PURPLE})`, boxShadow: '0 4px 16px rgba(82,92,235,0.35)' }}
                                >
                                    <Icon icon="lucide:plus" className="text-base" />
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-500 mt-1.5" style={{ textAlign: isAr ? 'right' : 'left' }}>
                                {isAr ? 'الحد الأقصى 5 وحدات' : 'Maximum 5 units per order'}
                            </p>
                        </Field>

                        <Field id="notes" label={isAr ? 'ملاحظات إضافية' : 'Additional Notes'} icon="lucide:message-square">
                            <TextareaField
                                id="notes"
                                placeholder={isAr ? 'أي تفاصيل أو متطلبات خاصة...' : 'Any specific requirements or details...'}
                                onChange={(e) => setNotes(e.target.value)}
                                dir={dir}
                            />
                        </Field>

                        <p className="text-[10px] text-gray-500 flex items-center gap-1" style={{ justifyContent: isAr ? 'flex-end' : 'flex-start' }}>
                            <span style={{ color: ACCENT_PURPLE }}>*</span>
                            {isAr ? 'الحقول المعلّمة مطلوبة' : 'Fields marked with * are required'}
                        </p>

                        {/* Error message */}
                        <AnimatePresence>
                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -8, height: 0 }}
                                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                                    exit={{ opacity: 0, y: -8, height: 0 }}
                                    className="flex items-center gap-2 p-3 rounded-xl text-sm text-red-400"
                                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
                                >
                                    <Icon icon="lucide:alert-circle" className="flex-shrink-0" />
                                    {error}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Submit button */}
                        <motion.button
                            type="submit"
                            disabled={submitting}
                            whileHover={{ scale: submitting ? 1 : 1.02, y: submitting ? 0 : -2 }}
                            whileTap={{ scale: 0.98 }}
                            className="relative w-full py-4 rounded-2xl text-white font-black text-sm overflow-hidden flex items-center justify-center gap-2 mt-1"
                            style={{
                                background: submitting
                                    ? 'linear-gradient(135deg, rgba(82,92,235,0.5), rgba(168,85,247,0.5))'
                                    : `linear-gradient(135deg, ${ACCENT}, ${ACCENT_PURPLE})`,
                                boxShadow: submitting ? 'none' : '0 12px 40px rgba(82,92,235,0.4)',
                                cursor: submitting ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {/* Shine sweep */}
                            {!submitting && (
                                <div className="absolute inset-0 pointer-events-none"
                                    style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.12) 50%, transparent 70%)' }} />
                            )}
                            {submitting ? (
                                <>
                                    <svg viewBox="0 0 24 24" fill="none" className="animate-spin h-5 w-5">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                                        <path d="M4 12a8 8 0 018-8v8H4z" fill="currentColor" className="opacity-75" />
                                    </svg>
                                    {isAr ? 'جاري الإرسال...' : 'Submitting...'}
                                </>
                            ) : (
                                <>
                                    <Icon icon="lucide:send" className="text-base" />
                                    {isAr ? 'إرسال الطلب' : 'Send Request'}
                                </>
                            )}
                        </motion.button>
                    </form>
                </motion.div>
            </div>

            {/* ── SUCCESS OVERLAY ─────────────────────────────────────────── */}
            <AnimatePresence>
                {submission && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center px-4"
                        style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(16px)' }}
                    >
                        <motion.div
                            initial={{ scale: 0.75, opacity: 0, y: 40 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                            className="rounded-3xl p-10 flex flex-col items-center text-center max-w-sm w-full"
                            style={{
                                background: 'linear-gradient(135deg, #0d0d1f, #12102a)',
                                border: '1px solid rgba(82,92,235,0.25)',
                                boxShadow: '0 40px 100px rgba(82,92,235,0.3)',
                            }}
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.15, type: 'spring', stiffness: 400, damping: 18 }}
                                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                                style={{
                                    background: 'rgba(16,185,129,0.1)',
                                    border: '2px solid rgba(16,185,129,0.35)',
                                    boxShadow: '0 0 40px rgba(16,185,129,0.2)',
                                }}
                            >
                                <Icon icon="lucide:check" className="text-emerald-400 text-4xl" />
                            </motion.div>
                            <h2 className="text-2xl font-black text-white mb-2">
                                {isAr ? 'تم الإرسال!' : 'Request Sent!'}
                            </h2>
                            <p className="text-sm text-gray-400 leading-relaxed font-medium">
                                {isAr
                                    ? 'تم استلام طلبك بنجاح. سنتواصل معك قريباً.'
                                    : "Your request has been received. We'll be in touch shortly."}
                            </p>
                            <div className="mt-6 w-full h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                                <motion.div
                                    className="h-full rounded-full"
                                    style={{ background: 'linear-gradient(90deg, #10b981, #06b6d4)' }}
                                    initial={{ width: '0%' }}
                                    animate={{ width: '100%' }}
                                    transition={{ duration: 3, ease: 'linear' }}
                                />
                            </div>
                            <p className="text-[10px] text-gray-600 mt-2 font-medium">
                                {isAr ? 'جاري إعادة التوجيه...' : 'Redirecting to services...'}
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}

export default RequestForm
